import { roundNumber } from './number';

export type CartLineDiscount = {
	grossPrice: number;
	netPrice: number;
	lineDiscountAmount: number;
	optionDiscountAmount: number;
	totalDiscountAmount: number;
	hasPromo: boolean;
	isOffered: boolean;
};

const buildCartLineDiscount = ({
	grossPrice,
	netPrice,
	lineDiscountAmount,
	optionDiscountAmount,
}: {
	grossPrice: number;
	netPrice: number;
	lineDiscountAmount: number;
	optionDiscountAmount: number;
}): CartLineDiscount => {
	const totalDiscountAmount = roundNumber(lineDiscountAmount + optionDiscountAmount);
	const resolvedGrossPrice = roundNumber(grossPrice);
	const resolvedNetPrice = roundNumber(Math.max(0, netPrice));

	return {
		grossPrice: resolvedGrossPrice,
		netPrice: resolvedNetPrice,
		lineDiscountAmount: roundNumber(lineDiscountAmount),
		optionDiscountAmount: roundNumber(optionDiscountAmount),
		totalDiscountAmount,
		hasPromo: resolvedGrossPrice !== resolvedNetPrice || totalDiscountAmount > 0,
		isOffered:
			totalDiscountAmount > 0 && resolvedGrossPrice <= totalDiscountAmount,
	};
};

const resolveCanonicalCartLineDiscount = (cartItem: any): CartLineDiscount | null => {
	const totalDiscountAmount = Number(cartItem?.totalLineDiscountAmount);
	const lineDiscountAmount = Number(cartItem?.lineDiscountAmount);
	const optionDiscountAmount = Number(cartItem?.optionsDiscountAmount);
	const grossLinePrice = Number(cartItem?.grossLinePrice);
	const fullPrice = Number(cartItem?.fullPrice);
	const netLinePrice = Number(cartItem?.netLinePrice);
	const numericPrice = Number(cartItem?.price);

	const hasCanonicalDiscount =
		Number.isFinite(totalDiscountAmount) ||
		Number.isFinite(lineDiscountAmount) ||
		Number.isFinite(optionDiscountAmount);
	const hasCanonicalPrice =
		Number.isFinite(grossLinePrice) || Number.isFinite(netLinePrice);
	const resolvedGrossPrice = Number.isFinite(grossLinePrice)
		? grossLinePrice
		: Number.isFinite(fullPrice)
			? fullPrice
			: NaN;
	const resolvedNetPrice = Number.isFinite(netLinePrice)
		? netLinePrice
		: Number.isFinite(numericPrice)
			? numericPrice
			: NaN;

	if (
		(!hasCanonicalDiscount && !hasCanonicalPrice) ||
		(!Number.isFinite(resolvedGrossPrice) && !Number.isFinite(resolvedNetPrice))
	) {
		return null;
	}

	return buildCartLineDiscount({
		grossPrice: Number.isFinite(resolvedGrossPrice) ? resolvedGrossPrice : resolvedNetPrice,
		netPrice: Number.isFinite(resolvedNetPrice) ? resolvedNetPrice : resolvedGrossPrice,
		lineDiscountAmount: Number.isFinite(lineDiscountAmount) ? lineDiscountAmount : 0,
		optionDiscountAmount: Number.isFinite(optionDiscountAmount) ? optionDiscountAmount : 0,
	});
};

export const sumOptionLevelPromotionAmount = (cartProduct: any): number => {
	const rowItems = cartProduct?.items;
	if (!Array.isArray(rowItems)) {
		return 0;
	}

	return rowItems.reduce((sum, row) => {
		const options = row?.options;
		if (!options || typeof options !== 'object') {
			return sum;
		}

		const rowPromo = Object.values(options).reduce((optionSum: number, option) => {
			const amount = Number((option as { productPromotionAmount?: number })
				?.productPromotionAmount);
			if (!Number.isFinite(amount) || amount <= 0) {
				return optionSum;
			}
			return optionSum + amount;
		}, 0);

		return roundNumber(sum + rowPromo);
	}, 0);
};

const resolveLineDiscountAmount = (cartItem: any): number => {
	const topLevelAmount = Number(cartItem?.productPromotionAmount);
	if (Number.isFinite(topLevelAmount) && topLevelAmount > 0) {
		return topLevelAmount;
	}

	const rawPrice = cartItem?.price;
	if (rawPrice && typeof rawPrice === 'object') {
		const objectAmount = Number(rawPrice.productPromotionAmount);
		if (Number.isFinite(objectAmount) && objectAmount > 0) {
			return objectAmount;
		}
	}

	return 0;
};

const resolveGrossPriceFromNumericLine = (
	cartItem: any,
	rawPrice: number,
	lineDiscountAmount: number,
	optionDiscountAmount: number
): number => {
	const grossFromFullPrice = Number(cartItem?.fullPrice);
	if (Number.isFinite(grossFromFullPrice)) {
		return grossFromFullPrice;
	}

	if (lineDiscountAmount > 0 || optionDiscountAmount > 0) {
		return roundNumber(rawPrice + lineDiscountAmount + optionDiscountAmount);
	}

	return rawPrice;
};

export const getCartLineDiscount = (
	cartItem: any,
	promoAmountFallback = 0
): CartLineDiscount => {
	const canonicalDiscount = resolveCanonicalCartLineDiscount(cartItem);
	if (canonicalDiscount) {
		return canonicalDiscount;
	}

	const optionDiscountAmount = sumOptionLevelPromotionAmount(cartItem);
	const lineDiscountAmount = resolveLineDiscountAmount(cartItem);
	const rawPrice = cartItem?.price;

	if (typeof rawPrice === 'number' && Number.isFinite(rawPrice)) {
		const grossPrice = resolveGrossPriceFromNumericLine(
			cartItem,
			rawPrice,
			lineDiscountAmount,
			optionDiscountAmount
		);

		if (
			grossPrice === rawPrice &&
			promoAmountFallback > 0 &&
			!lineDiscountAmount &&
			!optionDiscountAmount
		) {
			return buildCartLineDiscount({
				grossPrice,
				netPrice: Math.max(0, roundNumber(rawPrice - promoAmountFallback)),
				lineDiscountAmount: promoAmountFallback,
				optionDiscountAmount: 0,
			});
		}

		return buildCartLineDiscount({
			grossPrice,
			netPrice: rawPrice,
			lineDiscountAmount,
			optionDiscountAmount,
		});
	}

	if (typeof rawPrice === 'string') {
		const parsedPrice = parseFloat(rawPrice.replace(',', '.'));
		if (Number.isFinite(parsedPrice)) {
			return buildCartLineDiscount({
				grossPrice: parsedPrice,
				netPrice: parsedPrice,
				lineDiscountAmount: 0,
				optionDiscountAmount: 0,
			});
		}
	}

	if (rawPrice && typeof rawPrice === 'object') {
		const grossPrice = Number(rawPrice.fullPrice);
		const productPrice = Number(rawPrice.productPrice);
		const optionsPrice = Number(rawPrice.optionsPrice);
		const resolvedGrossPrice = Number.isFinite(grossPrice)
			? grossPrice
			: roundNumber(
					(Number.isFinite(productPrice) ? productPrice : 0) +
						(Number.isFinite(optionsPrice) ? optionsPrice : 0)
				);
		const resolvedLineDiscountAmount =
			lineDiscountAmount || Number(rawPrice.productPromotionAmount) || 0;

		return buildCartLineDiscount({
			grossPrice: resolvedGrossPrice,
			netPrice: Math.max(
				0,
				roundNumber(
					resolvedGrossPrice -
						resolvedLineDiscountAmount -
						optionDiscountAmount
				)
			),
			lineDiscountAmount: resolvedLineDiscountAmount,
			optionDiscountAmount,
		});
	}

	return buildCartLineDiscount({
		grossPrice: 0,
		netPrice: 0,
		lineDiscountAmount: 0,
		optionDiscountAmount: 0,
	});
};

export const getCartLineGrossPrice = (cartItem: any): number =>
	getCartLineDiscount(cartItem).grossPrice;

export const getCartLineNetPrice = (cartItem: any, promoAmountFallback = 0): number =>
	getCartLineDiscount(cartItem, promoAmountFallback).netPrice;

export const getCartLineMarketingDiscountAmount = (cartItem: any): number =>
	getCartLineDiscount(cartItem).totalDiscountAmount;
